/**
 * Package Expired Trigger Component
 *
 * Trigger: "an expired package"
 * Shows: Package multi-select (to filter by specific packages) + Stripe plan select
 *
 * In v2, this trigger allows selecting one or more packages from
 * currentPlatform.simplePackages, stored in reminderTypeId as an array of IDs.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import {
  FormSellableItemsMultiSelect,
  useSellableItems,
} from 'src/shared/Forms/Selects/SellableItemsMultiSelect';

import { TriggerComponentProps } from '../automationTriggers';

export const PackageExpiredTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { data: packages = [] } = useSellableItems('package');

  const selectedIds = Array.isArray(automation.reminderTypeId)
    ? automation.reminderTypeId
    : automation.reminderTypeId
      ? [automation.reminderTypeId]
      : [];

  const selectedPackages = packages.filter((pkg) =>
    selectedIds.some((id) => String(id) === pkg.value)
  );

  if (readOnly) {
    const parts: string[] = [t('automations.triggers.expiredPackage')];

    if (selectedPackages.length > 0) {
      const names = selectedPackages.map((p) => p.label).join(', ');
      parts.push(`- (${names})`);
    }

    if (automation.stripePlan?.nickname) {
      parts.push(
        t('automations.triggers.withPaymentPlan', {
          plan: automation.stripePlan.nickname,
        })
      );
    }

    return <span>{parts.join(' ')}</span>;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 400 }}>
        <FormSellableItemsMultiSelect
          name={`automations.${index}.reminderTypeId`}
          sellableType="package"
          placeholder={t('automations.triggers.anyPackage')}
        />
      </Box>
    </Stack>
  );
};
